import { apiService } from "@/services/api";
import { createItem, readItems } from "@directus/sdk";
import directus from "@/lib/directus";
import { ReloadModel } from "@/constants";
import { AppConfig } from "@/config/AppConfig";

export const affiliateService = {
  /**
   * Récupère le solde de commissions disponibles depuis le backend.
   * C'est le solde gagné et retirable.
   */
  async fetchAffiliateBalance(userId: string): Promise<{
    success: boolean;
    balance?: number;
    referralCount?: number;
    error?: string;
  }> {
    try {
      const response = await apiService.getUser(userId)

      if (response.success && response.data) {
        const balance = response.data.earnedCommission ?? response.data.totalEarnings ?? 0
        const referralCount = response.data.referredUsers?.length ?? response.data.referralCount ?? 0
        return {
          success: true,
          balance,
          referralCount,
        }
      }

      return {
        success: false,
        error: response.error || "Impossible de récupérer le solde",
      }
    } catch (error: any) {
      console.error("Error fetching affiliate balance:", error);
      return {
        success: false,
        error: error.message || "Erreur lors de la récupération du solde",
      };
    }
  },

  /**
   * Liste des filleuls d'un commercial.
   */
  async fetchReferrals(phoneNumber: string): Promise<{
    success: boolean;
    referrals?: { client_id: string; date_created: string }[];
    error?: string;
  }> {
    try {
      const referrals = (await directus.request(
        readItems("parrainage", {
          filter: { numero_commercial: { _eq: phoneNumber } },
          fields: ["client_id", "date_created"],
          sort: ["-date_created"],
        })
      )) as { client_id: string; date_created: string }[];

      return { success: true, referrals: referrals ?? [] };
    } catch (error: any) {
      console.error("Error fetching referrals:", error);
      return {
        success: false,
        error: error.message || "Erreur lors de la récupération des filleuls",
      };
    }
  },

  /**
   * Dépôts des filleuls, paginés.
   */
  async fetchReferralDeposits(
    phoneNumber: string,
    page = 1,
    limit = 10
  ): Promise<{
    success: boolean;
    deposits?: ReloadModel[];
    total?: number;
    error?: string;
  }> {
    try {
      const referralsResult = await this.fetchReferrals(phoneNumber);
      if (!referralsResult.success || !referralsResult.referrals?.length) {
        return { success: true, deposits: [], total: 0 };
      }

      const clientIds = referralsResult.referrals.map((r) => r.client_id);

      const result = (await directus.request(
        readItems("depot", {
          filter: { account_id: { _in: clientIds } },
          fields: ["id", "montant", "nom", "numero_commercial", "date_created"],
          sort: ["-date_created"],
          limit,
          page,
          meta: "filter_count",
        })
      )) as any;

      const deposits = (result?.data ?? result ?? []) as ReloadModel[];
      const total = result?.meta?.filter_count ?? deposits.length;

      return { success: true, deposits, total };
    } catch (error: any) {
      console.error("Error fetching referral deposits:", error);
      return {
        success: false,
        error: error.message || "Erreur lors de la récupération des dépôts",
      };
    }
  },

  /**
   * Soumet une demande de retrait de commissions.
   */
  async requestWithdrawal(
    userId: string,
    amount: number,
    _phoneNumber: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await apiService.processReferralWithdrawal(userId, amount);
      if (result.success) {
        return { success: true };
      }
      return {
        success: false,
        error: result.error || "Échec de la demande de retrait",
      };
    } catch (error: any) {
      console.error("Error requesting affiliate withdrawal:", error);
      return {
        success: false,
        error: error.message || "Erreur lors de la demande de retrait",
      };
    }
  },

  /**
   * Crée la relation commerciale d'un utilisateur qui n'en a pas.
   * Le numéro devient son code de parrainage (commercial_phoneNumber).
   */
  async createCommercialRelation(data: {
    userId: string;
    phoneNumber: string;
    nom?: string;
    prenom?: string;
  }): Promise<{ success: boolean; balance?: number; error?: string }> {
    try {
      // Le code de parrainage doit être unique : on refuse un numéro déjà pris.
      const existing = (await directus.request(
        readItems("commercial_relations", {
          filter: { commercial_phoneNumber: { _eq: data.phoneNumber } },
          fields: ["id", "user_id", "balance"],
          limit: 1,
        })
      )) as { id: string; user_id: string; balance?: number }[];
      if (existing?.[0]) {
        if (existing[0].user_id !== data.userId) {
          return { success: false, error: "Ce numéro est déjà utilisé par un autre parrain" };
        }
        return { success: true, balance: existing[0].balance ?? 0 };
      }

      const relation = (await directus.request(
        createItem("commercial_relations", {
          user_id: data.userId,
          commercial_phoneNumber: data.phoneNumber,
          nom: data.nom,
          prenom: data.prenom,
          application: AppConfig.commercialApplicationId,
        })
      )) as { balance?: number };

      return { success: true, balance: relation?.balance ?? 0 };
    } catch (error: any) {
      const message = error?.errors?.[0]?.message || error?.message;
      console.error("Error creating commercial relation:", message, error);
      return {
        success: false,
        error: message || "Impossible de créer votre espace parrain",
      };
    }
  },
};
